/**
 * DataGrid — sortier- und filterbares Tabellen-Grid mit ein-/ausblendbaren
 * Spalten (TanStack Table v8), Aufbau und Bedienung identisch zur
 * Referenz-Implementierung in rmnc-personal (ui/data-grid.tsx), gestylt
 * mit den Technik-Primitiven (TableWrapper, TH_/TR_/TD_CLASS aus ui.tsx).
 *
 * Verwendung: Spalten als ColumnDef<T> definieren (header = deutscher Titel
 * als String, damit die Spalten-Auswahl ihn anzeigen kann), Daten rein —
 * Sortierung, Schnellfilter und Spaltenauswahl kommen aus dem Grid.
 *
 * Spalten-Sichtbarkeit wird pro `storageKey` in localStorage persistiert.
 */
import { useEffect, useState, type ReactNode } from "react"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, Search } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui-shell/dropdown-menu"
import { Button, Input, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS } from "@/components/ui"
import { cn } from "@/lib/utils"

const STORAGE_PREFIX = "datagrid-columns:"

function loadVisibility(storageKey?: string): VisibilityState {
  if (!storageKey) return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
    return raw ? (JSON.parse(raw) as VisibilityState) : {}
  } catch {
    return {}
  }
}

export interface DataGridProps<T> {
  columns: ColumnDef<T, unknown>[]
  data: T[]
  /** Persistenz-Schlüssel für die Spalten-Sichtbarkeit (localStorage). */
  storageKey?: string
  /** Startsortierung, z. B. [{ id: "name", desc: false }]. */
  initialSorting?: SortingState
  /** Schnellfilter über alle sichtbaren Spalten (Default: an). */
  enableQuickFilter?: boolean
  filterPlaceholder?: string
  emptyMessage?: string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string | undefined
  /** Zusätzliche Elemente rechts in der Grid-Werkzeugleiste. */
  toolbar?: ReactNode
  className?: string
}

export function DataGrid<T>({
  columns,
  data,
  storageKey,
  initialSorting = [],
  enableQuickFilter = true,
  filterPlaceholder = "Filtern …",
  emptyMessage = "Keine Einträge gefunden.",
  onRowClick,
  rowClassName,
  toolbar,
  className,
}: DataGridProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  const [globalFilter, setGlobalFilter] = useState("")
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    loadVisibility(storageKey),
  )

  useEffect(() => {
    if (!storageKey) return
    try {
      window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(columnVisibility))
    } catch {
      // localStorage voll/gesperrt — Sichtbarkeit gilt dann nur für die Sitzung
    }
  }, [columnVisibility, storageKey])

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, columnVisibility },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Schnellfilter: case-insensitive Substring über den Accessor-Wert
    globalFilterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId)
      if (value == null) return false
      return String(value).toLowerCase().includes(String(filterValue).toLowerCase())
    },
  })

  const hideableColumns = table.getAllLeafColumns().filter((col) => col.getCanHide())
  const visibleColumnCount = table.getVisibleLeafColumns().length

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {enableQuickFilter && (
          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-9 pl-8"
              placeholder={filterPlaceholder}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          {toolbar}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="secondary" className="h-9">
                <Columns3 className="size-4" /> Spalten
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Spalten anzeigen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hideableColumns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(checked) => column.toggleVisibility(!!checked)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sorted = header.column.getIsSorted()
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        TH_CLASS,
                        canSort && "cursor-pointer select-none",
                        (header.column.columnDef.meta as { headClassName?: string } | undefined)
                          ?.headClassName,
                      )}
                      aria-sort={
                        sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : undefined
                      }
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <span className="inline-flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            (sorted === "asc" ? (
                              <ArrowUp className="size-3.5" />
                            ) : sorted === "desc" ? (
                              <ArrowDown className="size-3.5" />
                            ) : (
                              <ChevronsUpDown className="size-3.5 opacity-40" />
                            ))}
                        </span>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={visibleColumnCount} className="py-10 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row: Row<T>) => (
              <tr
                key={row.id}
                className={cn(TR_CLASS, onRowClick && "cursor-pointer", rowClassName?.(row.original))}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      TD_CLASS,
                      (cell.column.columnDef.meta as { cellClassName?: string } | undefined)
                        ?.cellClassName,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>
    </div>
  )
}

export type { ColumnDef, SortingState }
